import React from "react";

import { Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";

import video from "../../assets/videos/articles/transition-background-video.mp4";
import img1 from "../../assets/images/articles/transitie/transition1.jpg";
import img2 from "../../assets/images/articles/transitie/transition2.png";
import img3 from "../../assets/images/articles/transitie/transition3.jpg";
import img4 from "../../assets/images/articles/transitie/transition4.png";

import downloadArticle from "../../assets/pdf/transition-spaces.pdf";

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "white",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 400,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid black",
      borderRadius: "1px",
    },
  })
);

function Transition() {
  return (
    <div className="transition">
      <div className="transition__backgroundVideo">
        <div className="transition__backgroundVideo__wrapper">
          <div className="transition__backgroundVideo__title">
            <h1 className="editH1">Transition Spaces</h1>
            <h2 className="editH2">Lars Marcoen | 14.07.2022</h2>
          </div>
        </div>
        <video src={video} width="100%" height="auto" loop autoPlay muted playsInline></video>
      </div>

      <div className="transition__container">
        <div className="transition__container__title">
          <h1 className="editH1">Transition Spaces</h1>
          <h2 className="editH2">Lars Marcoen | 14.07.2022</h2>
        </div>

        <div className="transition__container__intro">
          <div className="transition__intro__text">
            The door, the entrance, the hallways and the gateway are the ideal spaces that reinforce a transition. These
            can symbolize a gateway to a new "realm", one with endless possibilities for self-expression. A place where
            the concept of time has never been invented and where everyone shares the same route to an unknown
            destination, taking place now and not tomorrow. As Koen Galle romantically describes in ‘Eidola’
            <HtmlTooltip title="C12. (2019). Eidôla. Die Keure" placement="bottom-start">
              <sup className="tooltip_ref">1</sup>
            </HtmlTooltip>
            . In an interview published in the first issue of Nachtplan & Horst
            <HtmlTooltip
              title="Nachtplan. (2019). Nachtplan. https://www.nachtplan.be/nachtplan-is"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">2</sup>
            </HtmlTooltip>
            , Arnaud De Wolf describes it as something poetic. "You stand in the queue, catch a little glimpse of the
            light and sound inside every time the door opens briefly, and in the end you undergo a kind of transition
            yourself upon entering. That is dramaturgy in its purest form"
            <HtmlTooltip
              title="Nachtplan. (2019). Nachtplan. https://www.nachtplan.be/nachtplan-is"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">2</sup>
            </HtmlTooltip>
            .
          </div>

          <div className="transition__intro__img">
            <img src={img1} alt="" />
            <p> @Simon Breynaert </p>
          </div>
        </div>

        <div className="transition__container__body">
          <div className="transition__body__text">
            <div>
              This transition space is necessary to present the dance floor as separate from the outside world.
              Transition is an in-between state, within architecture it is defined as a link or connection between two
              (or more) enclosed spaces. An architectural space feels incomplete without transitional spaces. These
              spaces can create connections that are both aesthetic and functional. They contribute to the spatial
              quality of the space, in any setting or environment. Their role is diverse and can be functional, social,
              symbolic and visually enhancing. Within Daniella Szauter's
              <HtmlTooltip
                title="Szauter, D. (2018). Transition Spaces. Műszaki Tudományos Közlemények, 9(1), 223–226. https://doi.org/10.33894/mtk-2018.09.51"
                placement="bottom-start"
              >
                <sup className="tooltip_ref">3</sup>
              </HtmlTooltip>{" "}
              research, it has become clear that this transition has a great influence on human behavior, which has an
              impact on the design or building. It is often the environment that a person first encounters when entering
              a building. It provides an important first impression that is determined by the quality of design and
              lighting
              <HtmlTooltip
                title="Szauter, D. (2018). Transition Spaces. Műszaki Tudományos Közlemények, 9(1), 223–226. https://doi.org/10.33894/mtk-2018.09.51"
                placement="bottom-start"
              >
                <sup className="tooltip_ref">3</sup>
              </HtmlTooltip>
              . Transitional spaces are necessary components because they provide both the entrance and a means of
              moving through a building. No building, except the most basic shell, can exist without such spaces. They
              play an important role in guiding and impressing those who move into transitional spaces. Some are
              designed to create stunning visual effects, social or circulation spaces, others to perform more mundane
              functions
              <HtmlTooltip
                title="Pitts, A. (2008). Building Transition Spaces, Comfort and Energy Use. Conference on Passive and Low Energy Architecture."
                placement="bottom-start"
              >
                <sup className="tooltip_ref">4</sup>
              </HtmlTooltip>
              . Within the physical place, the club, transition is necessary to provide the clubgoer with tension and
              the feeling of adrenaline and to separate the dance floor from the outside world. We walk through the
              transition space as if we are moving into a second, new world.
            </div>
          </div>
          <div className="transition__body__image">
            <img src={img2} alt="" />
          </div>
        </div>

        <div className="transition__container__bigImg">
          <img className="transition__big__img" src={img3} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="transition__container__body">
          <div className="transition__body__text">
            <div className="transition__text__4">
              In architecture, transition space refers to the threshold that is the connection between two enclosed
              spaces. It is an experiential space between the inner world and the outer world. The spaces can create
              subtle connections that are exploited and temporal experience can be defined by the architectural
              interpretation of the transition space. We experience transition so often that we are not even aware that
              the space is there. It is interesting to become aware of it. We experience them from macro to micro levels
              while being completely unaware of their presence. The design of transition spaces is important, not only
              to provide functional circulation routes, but also for reasons of aesthetics, for example by tactility,
              and as an emergency exit in case of emergencies. A transition space provides an opportunity for users to
              interact. … They are often spaces that invite interruption or breaks between the main activities taking
              place in the main spaces. Transition spaces help ease, or just intensify, the transition between the main
              spaces. A corridor connects spaces, directs circulation and helps make sense of the architectural
              environment
              <HtmlTooltip title="Boettger, T. (2014). Threshold Spaces. Birkhäuser." placement="bottom-start">
                <sup className="tooltip_ref">5</sup>
              </HtmlTooltip>
              .
            </div>
          </div>
          <div className="transition__body__image">
            <img src={img4} alt="" />
          </div>
        </div>

        <div className="transition__container__body">
          <div className="transition__body__text__middle" id="get__H2">
            These transitional spaces or passages in a building or architectural ensemble can be created through changes
            in light, sound, direction, surface and level
            <HtmlTooltip title="Boettger, T. (2014). Threshold Spaces. Birkhäuser." placement="bottom-start">
              <sup className="tooltip_ref">5</sup>
            </HtmlTooltip>
            . Because the space is experienced in a constant state of flux, it provides the opportunity to tackle it in
            a playful manner.
            <br /> <br />
            The transition that takes place is not only physical but also psychological, through the experience you
            have. In a temple, for example, the transition space has a spatial typology of procession and is very
            prominent. This transitional space, acting as a passageway, leads to an important destination. The path to
            that destination can become a procession or a ritual
            <HtmlTooltip
              title="Singh, R. (2015). Importance and Role in Indian Architecture. Understanding Transition Spaces."
              placement="bottom-start"
            >
              <sup className="tooltip_ref">6</sup>
            </HtmlTooltip>
            .
          </div>
        </div>

        <div className="transition__container__body">
          <div className="transition__body__text__middle middle__center" id="get__H2">
            <div className="display__flex">
              <div className="article__download__button">
                <a href={downloadArticle} download="article_transition-spaces_Lars-Marcoen">
                  {" "}
                  Download Article
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="transition__container__body">
          <div className="transition__body__text__middle" id="get__H2">
            <h2>Sources</h2>
            C12. (2019). Eidôla. Die Keure
            <br />
            <br />
            Nachtplan. (2019). Nachtplan. https://www.nachtplan.be/nachtplan-is
            <br />
            <br />
            Szauter, D. (2018). Transition Spaces. Műszaki Tudományos Közlemények, 9(1), 223–226.
            https://doi.org/10.33894/mtk-2018.09.51
            <br />
            <br />
            Pitts, A. (2008). Building Transition Spaces, Comfort and Energy Use. Conference on Passive and Low Energy
            Architecture.
            <br />
            <br />
            Boettger, T. (2014). Threshold Spaces. Birkhäuser.
            <br />
            <br />
            Singh, R. (2015). Importance and Role in Indian Architecture. Understanding Transition Spaces.
          </div>
        </div>
      </div>
    </div>
  );
}

export default Transition;
